import { useEffect, useState } from 'react'
import {
  getOrgCapabilities,
  setOrgCapability,
  OrgCapabilities,
  CapabilityValue
} from '../lib/adminClient'
import {
  CAPABILITY_CATEGORIES,
  CAPABILITIES,
  Capability,
  capabilityKind
} from '../lib/capabilityCatalog'

const PRODUCTS_KEY = 'products'
const PRODUCT_CAPS = CAPABILITIES.filter((c) => c.categoryKey === PRODUCTS_KEY)
const OTHER_CATEGORIES = CAPABILITY_CATEGORIES.filter((c) => c.key !== PRODUCTS_KEY)

const INHERIT_LABEL = 'Inherits (plan / per-user)'

// Human-readable form of an ORG override value for display next to a control.
function showOverride(v: CapabilityValue | undefined): string {
  if (v === undefined) return INHERIT_LABEL
  if (v === true) return 'On'
  if (v === false) return 'Off'
  return String(v)
}

interface RowProps {
  cap: Capability
  override: CapabilityValue | undefined
  set: boolean
  busy: boolean
  onSet: (value: CapabilityValue) => void
  onClear: () => void
}

// Tri-state for boolean capabilities: On / Off / Inherit. "Inherit" clears the
// org override so the capability falls through to plan and per-user resolution.
function BooleanRow({ override, set, busy, onSet, onClear }: RowProps) {
  const on = override === true
  const off = override === false
  return (
    <div className="flex items-center gap-1 rounded-md border border-white/10 overflow-hidden text-[11px]">
      <button
        disabled={busy}
        onClick={() => onSet(true)}
        className={`px-2.5 py-1 ${set && on ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
      >
        On
      </button>
      <button
        disabled={busy}
        onClick={() => onSet(false)}
        className={`px-2.5 py-1 border-l border-white/10 ${set && off ? 'bg-red-500/20 text-red-300' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
      >
        Off
      </button>
      <button
        disabled={busy}
        onClick={onClear}
        title="Clear the org override and inherit from plan and per-user"
        className={`px-2.5 py-1 border-l border-white/10 ${!set ? 'bg-white/10 text-slate-200' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
      >
        Inherit
      </button>
    </div>
  )
}

function NumberRow({ override, set, busy, onSet, onClear }: RowProps) {
  const [draft, setDraft] = useState(typeof override === 'number' ? String(override) : '')
  // Keep the input in step with server truth when it changes underneath us.
  useEffect(() => {
    setDraft(typeof override === 'number' ? String(override) : '')
  }, [override])

  function commit() {
    const n = Number(draft)
    if (draft.trim() === '' || Number.isNaN(n)) return
    onSet(n)
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit() }}
        placeholder={set ? '' : 'inherit'}
        className="w-24 bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-center focus:outline-none focus:border-accent"
      />
      <button
        disabled={busy}
        onClick={commit}
        className="px-2.5 py-1 rounded-md border border-white/10 text-[11px] text-slate-200 hover:bg-white/5 disabled:opacity-40"
      >
        Set
      </button>
      {set && (
        <button
          disabled={busy}
          onClick={onClear}
          className="text-[11px] text-slate-400 hover:text-slate-200 underline underline-offset-2 disabled:opacity-40"
        >
          Inherit
        </button>
      )}
    </div>
  )
}

function StringRow({ override, set, busy, onClear }: RowProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-300">{showOverride(override)}</span>
      <span className="text-[10px] text-slate-600">(read-only)</span>
      {set && (
        <button
          disabled={busy}
          onClick={onClear}
          className="text-[11px] text-slate-400 hover:text-slate-200 underline underline-offset-2 disabled:opacity-40"
        >
          Inherit
        </button>
      )}
    </div>
  )
}

export default function OrgAccessPanel({
  org,
  onClose
}: {
  org: { id: string; name: string; slug: string }
  onClose: () => void
}) {
  const [data, setData] = useState<OrgCapabilities | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setLoadError(null)
    void getOrgCapabilities(org.id).then((res) => {
      if (!alive) return
      if (!res.ok) {
        setLoadError(res.error || `Could not load org access (HTTP ${res.status}).`)
        setLoading(false)
        return
      }
      setData({ orgId: res.data.orgId, overrides: res.data.overrides })
      setLoading(false)
    })
    return () => { alive = false }
  }, [org.id])

  async function apply(key: string, value: CapabilityValue | null) {
    setSavingKey(key)
    setActionError(null)
    const res = await setOrgCapability(org.id, key, value)
    setSavingKey(null)
    if (!res.ok) {
      setActionError(`${key}: ${res.error || `save failed (HTTP ${res.status})`}`)
      return
    }
    // The PUT returns the refreshed override set, so we adopt it verbatim.
    setData({ orgId: res.data.orgId, overrides: res.data.overrides })
  }

  const overrides = data?.overrides ?? {}
  const overrideCount = Object.keys(overrides).length

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-6 overflow-y-auto" onClick={onClose}>
      <div className="glass-card rounded-2xl p-6 w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Access &amp; licensing</div>
            <h2 className="text-lg font-semibold mt-1">{org.name}</h2>
            <div className="text-[11px] text-slate-500">{org.slug} · {org.id.slice(0, 18)}…</div>
          </div>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>

        <div className="text-[11px] text-slate-500 leading-relaxed mb-5">
          Entitlements resolve tier &lt; org &lt; user. What you set here applies to the whole
          organization, but a per-user override can still sit on top of it. Anything you leave on
          Inherit falls through to the plan and the per-user resolution.
        </div>

        {loading && (
          <div className="py-16 text-center text-sm text-slate-500">Loading this org’s access…</div>
        )}

        {!loading && loadError && (
          <div className="rounded-xl p-4 text-sm bg-red-500/10 border border-red-500/30 text-red-200">
            {loadError}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-5 text-xs text-slate-400">
              <span>{overrideCount} {overrideCount === 1 ? 'capability set for this org' : 'capabilities set for this org'}</span>
              <span className="text-slate-500">·</span>
              <span>everything else inherits</span>
            </div>

            {actionError && (
              <div className="mb-4 rounded-md px-3 py-2 text-xs bg-red-500/10 border border-red-500/30 text-red-200">
                {actionError}
              </div>
            )}

            {/* Apps — the four products as prominent whole-app licensing controls. */}
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-1">Apps</div>
              <div className="text-[11px] text-slate-500 mb-3">License a whole app for this org, explicitly withhold it, or inherit from the plan and per-user resolution.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PRODUCT_CAPS.map((cap) => {
                  const override = overrides[cap.key]
                  const set = cap.key in overrides
                  const on = override === true
                  const busy = savingKey === cap.key
                  return (
                    <div key={cap.key} className={`rounded-xl border p-4 ${set && on ? 'border-white/10 bg-white/[0.03]' : 'border-white/10 bg-black/20'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-100 flex items-center gap-2">
                            {cap.label}
                            {set ? (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">set for this org</span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-slate-400">Inherits</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{cap.description}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-1 rounded-md border border-white/10 overflow-hidden text-[11px] w-fit">
                        <button
                          disabled={busy}
                          onClick={() => apply(cap.key, true)}
                          className={`px-2.5 py-1 ${set && on ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
                          title="License this app for the org"
                        >
                          On
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => apply(cap.key, false)}
                          className={`px-2.5 py-1 border-l border-white/10 ${set && !on ? 'bg-red-500/20 text-red-300' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
                          title="Explicitly not licensed — users see the greyed upsell"
                        >
                          Off
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => apply(cap.key, null)}
                          className={`px-2.5 py-1 border-l border-white/10 ${!set ? 'bg-white/10 text-slate-200' : 'text-slate-400 hover:bg-white/5'} disabled:opacity-40`}
                          title="Inherit from plan and per-user resolution"
                        >
                          Inherit
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Everything else, grouped by category. */}
            {OTHER_CATEGORIES.map((cat) => {
              const items = CAPABILITIES.filter((c) => c.categoryKey === cat.key)
              if (items.length === 0) return null
              return (
                <div key={cat.key} className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-0.5">{cat.label}</div>
                  <div className="text-[11px] text-slate-500 mb-3">{cat.blurb}</div>
                  <div className="rounded-lg border border-white/5 overflow-hidden">
                    {items.map((cap) => {
                      const kind = capabilityKind(cap)
                      const override = overrides[cap.key]
                      const set = cap.key in overrides
                      const busy = savingKey === cap.key
                      const rowProps: RowProps = {
                        cap,
                        override,
                        set,
                        busy,
                        onSet: (v) => apply(cap.key, v),
                        onClear: () => apply(cap.key, null)
                      }
                      return (
                        <div key={cap.key} className={`flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5 last:border-b-0 ${set ? 'bg-accent/[0.03]' : ''}`}>
                          <div className="min-w-0">
                            <div className="text-sm text-slate-100 flex items-center gap-2">
                              {cap.label}
                              {cap.status && cap.status !== 'shipping' && (
                                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cap.status === 'beta' ? 'bg-indigo-500/15 text-indigo-300' : 'bg-amber-500/15 text-amber-300'}`}>
                                  {cap.status === 'beta' ? 'Beta' : 'Soon'}
                                </span>
                              )}
                              {set ? (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">set for this org</span>
                              ) : (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-slate-400">Inherits</span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {set ? 'Set for this org' : 'Inherits'} · <span className="text-slate-300">{showOverride(override)}</span>
                            </div>
                          </div>
                          <div className="shrink-0">
                            {kind === 'boolean' && <BooleanRow {...rowProps} />}
                            {kind === 'number' && <NumberRow {...rowProps} />}
                            {kind === 'string' && <StringRow {...rowProps} />}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}

            <div className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3">
              Changes save the moment you set or clear a row. Choosing Inherit clears the org override so the capability falls through to the plan and the per-user resolution. String capabilities are shown read-only here; change them per tier from the Subscriptions page.
            </div>
          </>
        )}
      </div>
    </div>
  )
}
